<template>
  <div>
    <canvas ref="resizeCanvas" style="display: none"></canvas>
    <file-input
      ref="fileUploadField"
      v-model="uploadFileTargets"
      multiple="true"
      class="hidden"
      label="file upload" />

    <div v-if="currentStep == 0" class="step-0 text-center">
      <h3 class="margin-remove text-bold">{{ tt("ls.fmUploader.dropFileTitle") }}</h3>
      <p class="margin-xl-bottom">
        {{ tu("ls.fmUploader.step0Explain") }}
      </p>
      <div class="flex width-expand flex-center">
        <div
          ref="dropzone"
          class="dropzone cursor-pointer padding-2xl-vertical flex-middle flex-center margin-inline-auto max-width-600 border-lead"
          :class="{ 'drop-highlight': isDraggingFiles }"
          @click="clickFileInput"
          @dragenter.prevent.stop="handleFileDragEnter"
          @dragover.prevent.stop=""
          @dragleave.prevent.stop="handleFileDragLeave"
          @drop.prevent.stop="handleFileDrop">
          {{ tu("ls.fmUploader.dropZoneText") }}
        </div>
      </div>
    </div>

    <div v-if="currentStep == 1" class="step-1">
      <div class="text-center flex-column flex-center flex-middle">
        <h3 class="margin-remove text-bold">{{ tt("ls.fmUploader.setContentTitle") }}</h3>
        <p class="margin-xl-bottom">
          {{ tu("ls.fmUploader.step1Explain") }}
        </p>

        <form-button
          v-tooltip="{ content: 'ls.fmUploader.setFileNamesAsTitleTooltip' }"
          class="margin-l-bottom"
          @click="setFileNamesAsTitles"
          >{{ t("ls.fmUploader.setFileNamesAsTitleCta") }}</form-button
        >
      </div>

      <grid class="upload-items">
        <grid-column
          v-for="(item, itemIndex) of uploadData"
          :span="6"
          :span-l="12"
          class="">
          <div class="upload-item-block flex relative">
            <icon-halo
              class="item-cancel-icon cursor-pointer"
              icon="x"
              theme="lead"
              halo-theme="bg"
              @click="removeUploadItem(itemIndex)"></icon-halo>

            <div class="preview-container padding-m">
              <img :src="item.objectUrl" alt="" />
            </div>
            <div class="form-container padding-m flex flex-middle">
              <spinner
                v-if="item.isLoading"
                :size="2"
                text="ls.fmUploader.itemUploadingExplain"></spinner>

              <h4 v-if="item.isComplete" class="margin-remove">
                <icon-text icon="check" class="c-success">{{
                  tu("ls.fmUploader.itemUploadedExplain")
                }}</icon-text>
              </h4>
              <div class="flex-column gap-m">
                <form-input
                  v-if="!item.isLoading && !item.isComplete"
                  v-model="item.title"
                  type="textarea"
                  label="ls.fmUploader.imageTitleLabel"
                  :max-characters="150"></form-input>
                <form-input
                  v-if="!item.isLoading && !item.isComplete"
                  v-model="item.featured"
                  type="checkbox"
                  label="תמונה מייצגת?"
                ></form-input>
              </div>

            </div>
          </div>
        </grid-column>
      </grid>

      <form-button
        class="width-expand margin-xl-top"
        size="l"
        :loading="isLoading"
        theme="success"
        icon-end="chevron-inline-end"
        text="ls.fmUploader.uploadAllCta"
        @click="createFounderMediaByState"></form-button>
    </div>

    <div v-if="currentStep == 2" class="step-2">
      <div class="text-center flex-column flex-center flex-middle">
        <h3 class="margin-xl-bottom text-bold">
          {{ tt("ls.fmUploader.finishStepTitle") }}
        </h3>
      </div>

      <grid class="upload-items">
        <grid-column v-for="(item, index) of uploadData" :span="6" :span-l="12" class="">
          <div class="upload-item-block flex relative">
            <div class="preview-container padding-m">
              <img :src="item.objectUrl" alt="" />
            </div>
            <div class="form-container padding-m flex flex-middle">
              <spinner
                v-if="item.isLoading"
                :size="2"
                text="ls.fmUploader.itemUploadingExplain"></spinner>
              <h4 v-if="item.isComplete" class="margin-remove">
                <icon-text icon="check" class="c-success">{{
                  tu("ls.fmUploader.itemUploadedExplain")
                }}</icon-text>
              </h4>

              <form-input
                v-if="!item.isLoading && !item.isComplete"
                v-model="item.featured"
                type="checkbox"
                label="ls.fmUploader.isFeaturedItem"
              ></form-input>
            </div>
          </div>
        </grid-column>
      </grid>
      <form-button
        v-if="currentStep === 1"
        class="width-expand margin-xl-top"
        size="l"
        :loading="isLoading"
        theme="success"
        icon-end="chevron-inline-end"
        text="ls.fmUploader.uploadAllCta"
        @click="createFounderMediaByState"></form-button>
      <icon-text
        v-if="hasUploadError"
        icon="warning-triangle"
        class="text-l c-danger"
        text="ls.fmUploader.someFilesHaveErrors"></icon-text>

      <p
        class="margin-xl-bottom c-success text-bold text-center block max-width-700 margin-auto-horizontal margin-2xl-top">
        {{ tu("ls.fmUploader.step2Explain") }}
      </p>
      <form-button
        class="width-expand margin-xl-top"
        size="l"
        :loading="isLoading"
        theme="success"
        icon="check"
        text="ls.fmUploader.finishCta"
        @click="finishProcess"></form-button>
    </div>

    <div v-if="internalMessage">
      <span>{{ internalMessage }}</span>
    </div>
    <div v-if="internalErrorMessage">
      <icon-text icon="warning-triangle" class="c-danger">
        {{ internalErrorMessage }}
      </icon-text>
    </div>
  </div>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import useMediaManager from "@/client/extensions/composition/useMediaManager";

export default {
  props: {
    maxImageWidth: {
      type: Number,
      default: 1920,
    },
    maxImageHeight: {
      type: Number,
      default: 1200,
    },
    founderId: {
      type: [Number, String],
      default: 0,
    },
  },
  emits: ["fmu:upload-complete"],
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);
    const mediaManager = useMediaManager(props, {});
    return { asyncOps, asyncOpsReady, asyncStatus, mediaManager };
  },
  data: function () {
    return {
      isLoading: false,
      isDraggingFiles: false,
      uploadFileTargets: false,
      uploadData: {},
      currentStep: 0,
      internalErrorMessage: false,
      internalMessage: false,
      hasUploadError: false,
    };
  },
  watch: {
    uploadFileTargets: {
      async handler(newVal) {
        if (!newVal) {
          return true;
        }
        this.internalErrorMessage = false;
        this.internalMessage = false;
        if (newVal && this.currentStep === 0) {
          this.currentStep = 1;
        }

        this.uploadData = {};
        // basic validation
        for (const file of newVal) {
          if (file.size / 1000000 > 50) {
            this.$s.ui.notification("ls.fmUploader.extremelyLargeFileError", "error", {
              contentParams: { maxSizeMB: config.mediaManager.maxFileSizeMB },
            });
            this.internalErrorMessage = this.t("ls.fmUploader.extremelyLargeFileError");
            return false;
          }

          if (!file.type.startsWith("image/") && ! file.type.startsWith("application/pdf")) {
            this.$s.ui.notification("ls.fmUploader.invalidFormat", "error");
            this.internalErrorMessage = this.t("ls.fmUploader.invalidFormat");
            return false;
          }
        }
        // for each file
        // make an upload data entry
        // resize, and save that file data in a seperate state
        let i = 0;
        for (const file of this.uploadFileTargets) {
          // create file data
          // create smaller file
          this.internalMessage = this.t("ls.form.mediaSelectField.processingOngoing");
          let resizedFile = await this.enforceFileSize(file);
          this.internalMessage = false;

          let objectUrl;
          if (file.type.startsWith("application/pdf")) {
            objectUrl = utilities.getAsset('images/logos/pdf.png')
          } else {
            objectUrl = URL.createObjectURL(resizedFile);
          }

          this.uploadData[i] = {
            file: file,
            resizedFile: resizedFile,
            title: "",
            featured: false,
            objectUrl: objectUrl,
            isComplete: false,
            hasError: false,
            isLoading: false,
          };

          i++;
        }

        this.isLoading = false;
      },
      deep: true,
      immediate: false,
    },
  },
  mounted() {
    this.reset();
  },
  methods: {
    clickFileInput() {
      this.$refs.fileUploadField.triggerClick()
    },
    handleFileDragEnter(event) {
      let dropDiv = this.$refs.dropzone;
      if (!dropDiv.contains(event.relatedTarget)) {
        this.isDraggingFiles = true;
      }
    },
    handleFileDragLeave(event) {
      let dropDiv = this.$refs.dropzone;
      if (!dropDiv.contains(event.relatedTarget)) {
        this.isDraggingFiles = false;
      }
    },
    handleFileDrop(event) {
      if (!event?.dataTransfer?.files) {
        return;
      }
      try {
        this.$refs.fileUploadField.$el.files = event.dataTransfer.files;
        this.uploadFileTargets = event.dataTransfer.files;
        this.isDraggingFiles = false;
      } catch (e) {
        console.log(e);
      }
    },
    setFileNamesAsTitles() {
      for (const [index, item] of Object.entries(this.uploadData)) {
        const dotIndex = item.file.name.lastIndexOf(".");
        item.title = item.file.name.substring(0, dotIndex);
      }
    },
    reset() {
      this.currentStep = 0;
      this.uploadFileTargets = false;
      this.uploadData = {};
    },
    async enforceFileSize(file) {
      if (! file.type.startsWith('image/')) {
        return file;
      }
      const canvas = this.$refs["resizeCanvas"];
      const maxWidth = this.maxImageWidth;
      const maxHeight = this.maxImageHeight;

      let resizeOnCanvas = (img) => {
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        // Create a canvas element
        const canvas = this.$refs["resizeCanvas"];
        const ctx = canvas.getContext("2d");

        // Resize the canvas to the new dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw the image on the canvas
        ctx.drawImage(img, 0, 0, width, height);
        return true;
      };
      let blobToFile = (blob) => {
        return new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now(),
        });
      };
      let imageLoadHandler = (img, resolve) => {
        resizeOnCanvas(img);
        // Convert the canvas content to a Blob object
        canvas.toBlob((blob) => {
          resolve(blobToFile(blob));
        }, file.type);
      };

      let result = await new Promise((resolve) => {
        let img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => imageLoadHandler(img, resolve);
      });

      return result;
    },
    async createMediaByItem(item) {
      item.isLoading = true;
      // do not re-upload items
      if (item.isComplete) {
        return true;
      }

      let uploadResult = await this.mediaManager.upload(item.resizedFile, { data: {} });
      if (!uploadResult) {
        item.isComplete = false;
        item.hasError = true;
        item.isLoading = false;
        return false;
      }

      // make the founder media
      let fmCreateResult = await this.asyncOps.asyncCall(
        "founder-media",
        {
          FounderId: this.founderId,
          title: item.title,
          featured: item.featured,
          MediaId: uploadResult.id,
        },
        { method: "post" }
      );
      if (fmCreateResult.hasError) {
        item.isComplete = false;
        item.hasError = true;
        item.isLoading = false;
        return false;
      }

      item.isComplete = true;
      item.isLoading = false;

      return true;
    },
    async createFounderMediaByState() {
      let isFullSuccess = true;
      let isAnySuccess = false;
      this.isLoading = true;
      for (const [index, item] of Object.entries(this.uploadData)) {
        let res = await this.createMediaByItem(item);

        if (res) {
          isAnySuccess = true;
        } else {
          isFullSuccess = false;
        }
      }

      if (!isFullSuccess) {
        this.hasUploadError = true;
      }
      this.isLoading = false;

      this.currentStep = 2;
      return true;
    },
    finishProcess() {
      this.$emit("fmu:upload-complete", { data: this.uploadData });
      this.reset();
    },
    removeUploadItem(index) {
      delete this.uploadData[index];
      // deleting from FileList in state is hard, and not required, it only ever gets re-written
    },
  },
};
</script>

<style scoped lang="scss">
.dropzone {
  width: 800px;
  flex: 0 0 800px;
  max-width: 100%;
  transition: background-color 150ms ease, color 150ms ease;

  &.drop-highlight {
    background-color: rgba(0, 0, 0, 0.7);
    color: var(--c-bg);
  }
}

.upload-item-block {
  color: var(--c-bg-1);
  background-color: var(--c-bg-5);
  border: 1px solid var(--c-gray-2);

  .preview-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-items: center;
    min-height: 150px;
    width: 150px;
    background-color: var(--c-bg);
    border-inline-end: 2px solid var(--c-gray-2);

    img {
      height: 100%;
      width: 100%;
      max-height: 150px;
      object-fit: contain;
    }
  }

  .item-cancel-icon {
    position: absolute;
    top: var(--margin-m);
    inset-inline-end: var(--margin-m);
  }
}
</style>
